/**
 * Liens de paiement (kanban #870) : contrat partagé par le backend (`paymentLinks/`), le mini-site
 * paiements (`apps/payments-site`, lot 6) et la modale du back-office (lot 7), SANS dépendance
 * runtime : ce module est exporté par le barrel (`index.ts`). Le mini-site importe ses types en
 * `import type` et ses constantes par le chemin du module (`@gozoki/api-types/src/payment-links`)
 * pour ne pas embarquer le barrel — et ses schémas Vine — dans son bundle. Point de vérité unique
 * des règles que le schéma Vine de `POST /payment-links` et le formulaire Zod de la modale
 * appliquent tous deux (bornes du montant et du libellé, validités) et du gabarit du SMS, que le
 * backend envoie et que la modale prévisualise.
 */

export const PAYMENT_LINK_STATUSES = [
  'created',
  'sent',
  'opened',
  'paid',
  'expired',
  'cancelled',
  'failed',
] as const
export type PaymentLinkStatus = (typeof PAYMENT_LINK_STATUSES)[number]

/**
 * Motif d'un lien, **déduit à la création** par le backend (`resolvePaymentLinkReason`), jamais
 * saisi : un client banni avec une dette reçoit une régularisation d'impayé, tout autre
 * destinataire `other`. L'ancienne valeur `order` (lot 5, jamais en prod) se lit `other`.
 */
export const PAYMENT_LINK_REASONS = ['unpaid_regularization', 'other'] as const
export type PaymentLinkReason = (typeof PAYMENT_LINK_REASONS)[number]

/**
 * Règle unique du motif (décision 14) : un client banni avec une dette (€) strictement positive
 * reçoit une régularisation d'impayé — motif que l'IPN lit pour débannir au paiement. Le backend
 * en déduit `reason` (`resolvePaymentLinkReason`), la modale du BO l'annonce avant l'envoi.
 * `unBanAmount` `null`/`undefined` (aucune dette ou dette masquée par les droits) : pas de
 * régularisation annoncée.
 */
export const isPaymentLinkRegularization = (recipient: {
  isBanned: boolean
  unBanAmount?: number | null
}): boolean =>
  recipient.isBanned && typeof recipient.unBanAmount === 'number' && recipient.unBanAmount > 0

/**
 * Statuts dans lesquels le lien peut encore être payé (donc renvoyé, annulé, ouvert). `failed`
 * signifie seulement qu'aucun canal n'a abouti : l'URL reste valable si elle est transmise
 * autrement (copie depuis le BO).
 */
export const PAYMENT_LINK_PAYABLE_STATUSES: readonly PaymentLinkStatus[] = [
  'created',
  'sent',
  'opened',
  'failed',
]

export const isPayablePaymentLinkStatus = (status: PaymentLinkStatus): boolean =>
  PAYMENT_LINK_PAYABLE_STATUSES.includes(status)

/**
 * Anomalies de règlement : l'argent est encaissé (Payzen l'a déjà débité) et le lien `paid`, mais
 * un humain doit regarder — rembourser ou confirmer. Portées par `payment_links.anomaly`, par
 * `payments.details.anomaly` de la ligne concernée, par l'audit `payment_link.anomaly`, par une
 * alerte email au pilotage et par le badge du back-office.
 * - `paid_after_cancellation` : lien annulé depuis le BO (souvent parce qu'un autre a été envoyé)
 *   puis payé depuis une page encore ouverte ;
 * - `paid_after_expiry` : payé après la date limite (page ouverte avant, job passé ou non) ;
 * - `regularization_without_ban` : régularisation d'impayé d'un client qui n'est plus banni — la
 *   dette a été réglée par ailleurs (autre lien, app, débannissement manuel) ;
 * - `duplicate_payment` : seconde transaction Payzen sur un lien déjà réglé.
 */
export const PAYMENT_LINK_ANOMALIES = [
  'paid_after_cancellation',
  'paid_after_expiry',
  'regularization_without_ban',
  'duplicate_payment',
] as const
export type PaymentLinkAnomaly = (typeof PAYMENT_LINK_ANOMALIES)[number]

/**
 * Libellé d'une anomalie, **seule table** : le backend l'écrit dans les lignes `payments`, le
 * journal du client, l'alerte email et Sentry ; le back-office l'affiche en badge. Une seule
 * formulation, pour que l'admin lise la même chose partout.
 */
export const PAYMENT_LINK_ANOMALY_LABELS: Record<PaymentLinkAnomaly, string> = {
  paid_after_cancellation: 'Payé après annulation, à vérifier',
  paid_after_expiry: 'Payé après expiration, à vérifier',
  regularization_without_ban: 'Régularisation d’un client non banni, à vérifier',
  duplicate_payment: 'Doublon encaissé, à rembourser',
}

/**
 * Fenêtres (jours, sur la date de création) du filtre « période » de l'écran Liens de paiement
 * (`GET /payment-links?period=`) ; sans `period`, tous les liens.
 */
export const PAYMENT_LINK_PERIODS = [7, 30, 90, 365] as const
export type PaymentLinkPeriod = (typeof PAYMENT_LINK_PERIODS)[number]

/**
 * Forme du jeton public (24 octets aléatoires en base64url = 32 caractères) : le backend refuse
 * (422) toute autre forme avant d'interroger la base ; le mini-site l'applique au chemin de l'URL
 * avant d'appeler l'API.
 */
export const PAYMENT_LINK_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22,64}$/

/**
 * `GET /public/payment-links/:token` (200) : ce que la page publique affiche, rien de plus. Le
 * motif (`reason`) n'en fait pas partie : la page n'affiche que le libellé en texte libre
 * (décision Baptiste du 25/09, le motif est déduit côté serveur au lot 7).
 */
export interface PublicPaymentLinkResponse {
  status: PaymentLinkStatus
  /** Euros. */
  amount: number
  /** Centimes, l'unité du formulaire Payzen. */
  amountCents: number
  currency: 'EUR'
  /** Texte libre saisi dans le back-office. */
  label: string
  /** « Prénom N. » */
  recipientDisplayName: string
  storeLabel: string | null
  /** ISO 8601. */
  expiresAt: string
  /** « mercredi 1 octobre 2026 à 14h05 », heure de Paris — même forme que l'email et le SMS. */
  expiresAtLabel: string
}

/** `POST /public/payment-links/:token/form-token` (200) : de quoi monter le formulaire Krypton. */
export interface PublicPaymentLinkFormTokenResponse {
  formToken: string
  /** `kr-public-key` du formulaire embarqué Krypton. */
  publicKey: string
  /** URL du client Krypton (`PAYZEN_FORM_URL`), à charger en `<script src>`. */
  endpoint: string
  mode: 'TEST' | 'PROD'
}

/**
 * 410 des deux routes publiques : le lien n'est plus payable (`paid`, `expired` ou `cancelled`),
 * la page affiche le message correspondant au statut.
 */
export interface PublicPaymentLinkGoneResponse {
  status: PaymentLinkStatus
  error: string
}

/** Autres erreurs des routes publiques : 404 inconnu, 422 jeton mal formé, 429 (`retryAfter` en secondes), 502 Payzen muet. */
export interface PublicPaymentLinkErrorResponse {
  error: string
  retryAfter?: number
}

/** Validités proposées par la modale (clés du schéma Vine et du formulaire). */
export const PAYMENT_LINK_VALIDITY_KEYS = ['48h', '7d', '30d'] as const
export type PaymentLinkValidityKey = (typeof PAYMENT_LINK_VALIDITY_KEYS)[number]

/** Durée en heures de chaque validité ; le type impose une entrée par clé, ni plus ni moins. */
export const PAYMENT_LINK_VALIDITY_HOURS = {
  '48h': 48,
  '7d': 7 * 24,
  '30d': 30 * 24,
} as const satisfies Record<PaymentLinkValidityKey, number>

/** Plafond décidé par Baptiste le 24/09 : 100 € par lien. */
export const PAYMENT_LINK_MAX_AMOUNT_EUROS = 100
export const PAYMENT_LINK_MIN_AMOUNT_EUROS = 0.01

/**
 * Libellé libre, visible par le destinataire (email, SMS, page de paiement) : 3 caractères au
 * moins (après trim) pour qu'il dise quelque chose, 140 au plus pour tenir dans un SMS.
 */
export const PAYMENT_LINK_LABEL_MIN_LENGTH = 3
export const PAYMENT_LINK_LABEL_MAX_LENGTH = 140

/**
 * Date limite telle que l'email et le SMS l'écrivent (`mercredi 1 octobre 2026 à 14h05`) : format
 * luxon, en heure de Paris et en français.
 */
export const PAYMENT_LINK_EXPIRY_FORMAT = "cccc d LLLL yyyy 'à' HH'h'mm"
export const PAYMENT_LINK_EXPIRY_ZONE = 'Europe/Paris'

/**
 * Corps du SMS : court, l'URL en dernier pour rester cliquable sur tous les téléphones. Montant et
 * date limite arrivent déjà formatés (`formatCentsAsEuros`, `PAYMENT_LINK_EXPIRY_FORMAT`).
 */
export const paymentLinkSmsText = (parts: {
  label: string
  amountLabel: string
  expiryLabel: string
  url: string
}): string =>
  `Lucie : ${parts.label} - ${parts.amountLabel} à régler avant le ${parts.expiryLabel}. Paiement sécurisé : ${parts.url}`
